import React, {useState, useEffect, useCallback} from "react";
import {Form, Input, Button, Row, Col, Space, Typography, Modal, Tooltip} from "antd";
import {useSelector} from "react-redux";
import {CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined, AimOutlined, EnvironmentFilled} from "@ant-design/icons";
import peopleService from "../../services/PeopleService";
import IneValidator from "../../services/IneValidator";
import electorKeyValidator from "../../services/ElectorKeyValidator";
import theme from "../../theme";
import zipCodeRangeService from "../../services/ZipCodeRangeService";
import formatDate from "../../utils/dateUtils";
import {MapContainer, TileLayer} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const {Title, Text} = Typography;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const PersonEditForm = ({person, mode = "view", onClose}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [modalMessage, setModalMessage] = useState("");
    const section = useSelector((state) => state.section);

    const [zipCodeRange, setzipCodeRange] = useState({});
    const [areaCodes, setAreaCodes] = useState([]);
    const [mapCenter, setMapCenter] = useState([19.4326, -99.1332]);
    const [mapKey, setMapKey] = useState(0);
    const auth = useSelector((state) => state.auth || {});
    const municipalityId = auth.user?.municipalityId || 0;
    const entityId = auth.user?.entityId || 0;
    const districtId = auth.user?.districtId || 0;
    const districtLocal = auth.user?.districtLocal || null;

    const isEditMode = mode === "edit";

    useEffect(() => {
        fetch('/areaCodes.json')
            .then(res => res.json())
            .then(data => setAreaCodes(data))
            .catch(err => console.error('Error loading area codes:', err));
    }, []);

    useEffect(() => {
        if (person) {
            form.setFieldsValue({
                electorKey: person.electorKey,
                name: person.name,
                firstLastName: person.firstLastName,
                secondLastName: person.secondLastName || "",
                phone: person.phone || "",
                street: person.street,
                exteriorNumber: person.exteriorNumber,
                interiorNumber: person.interiorNumber || "",
                reference: person.reference || "",
                referredByIneKey: person.referredByIneKey || "",
                latLng: person.latLng || "",
                zipCode: person.zipCode,
                neighborhood: person.neighborhood,
                municipalityName: section.municipalityName || "",
                entityName: section.entityName || "",
                section: person.sectionId,
            });
            
            if (person.latLng) {
                setMapCenter(person.latLng.split(',').map(Number));
                setMapKey(prev => prev + 1);
            }
        }
    }, [person, form, section.municipalityName, section.entityName]);


    useEffect(() => {
        const fetchSectionData = async () => {
            try {
                const filters = {
                    entityId: {value: entityId, operator: "eq"},
                    municipalityId: {value: municipalityId, operator: "eq"},
                };
                const responseZipCodeRange = await zipCodeRangeService.getAll(filters);
                if (responseZipCodeRange.data.length > 0) {
                    setzipCodeRange(responseZipCodeRange.data[0]);
                }
            } catch (error) {
                console.error("Error fetching section data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSectionData().catch(console.error);
    }, [setzipCodeRange, entityId, municipalityId]);

    const validateIneMatch = () => {
        const electorKey = form.getFieldValue('electorKey');
        const name = form.getFieldValue('name');
        const firstLastName = form.getFieldValue('firstLastName');
        const secondLastName = form.getFieldValue('secondLastName');
        
        if (electorKey && name && firstLastName && secondLastName) {
            const isValid = IneValidator.validateNameMatch(electorKey, name, firstLastName, secondLastName);
            if (isValid === false) {
                return Promise.reject('No coincide con la clave INE');
            }
        }
        return Promise.resolve();
    };

    const handleSubmit = useCallback(
        async (values) => {
            if (!isEditMode) return;

            setLoading(true);

            const formData = {
                ...person,
                ...values,
                phone: values.phone.replace(/\D/g, ''),
                entityId: entityId,
                municipalityId: municipalityId,
                districtId: districtId,
                districtLocal: districtLocal,
                sectionId: parseInt(values.section),
                interiorNumber: values.interiorNumber == null || values.interiorNumber.trim() === ""
                    ? null
                    : values.interiorNumber,
            };

            try {
                await peopleService.update(person.id, formData);
                showModal("success", "Registro actualizado exitosamente.");
                setTimeout(() => {
                    setModalVisible(false);
                    onClose();
                }, 1500);
            } catch (error) {
                if (error.errorCode === "VALIDATION_ERROR" || error.errorCode === "BAD_REQUEST") {
                    const validationErrors = error.details;
                    const errorMessages = validationErrors.flatMap((errorObj) => Object.values(errorObj));
                    showModal(
                        "error",
                        <>
                            <p><b>Por favor corrige los siguientes errores:</b></p>
                            <ul style={{textAlign: "left"}}>
                                {errorMessages.map((msg, index) => (
                                    <li key={index}>{msg}</li>
                                ))}
                            </ul>
                        </>
                    );
                } else if (error.errorCode === "CONFLICT") {
                    showModal("error", error.details || error.message || "Error al actualizar el registro.");
                } else {
                    showModal("error", error.message || "Error al actualizar el registro.");
                }
            } finally {
                setLoading(false);
            }
        },
        [person, entityId, municipalityId, districtId, districtLocal, isEditMode, onClose]
    );

    const showModal = (type, message) => {
        setModalType(type);
        setModalMessage(message);
        setModalVisible(true);
    };

    const requestLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setMapCenter([position.coords.latitude, position.coords.longitude]);
                    setMapKey(prev => prev + 1);
                },
                (error) => {
                    console.error('Error obteniendo ubicación:', error);
                }
            );
        }
    };

    return (
        <div
            style={{
                padding: "16px",
                background: theme.colors.card,
                borderRadius: "8px",
            }}
        >
            <Title level={2} style={{textAlign: "center", marginBottom: "20px"}}>
                {isEditMode ? "Editar Convencido" : "Ver Convencido"}
            </Title>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark={false}
                style={{maxWidth: "100%"}}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                    }
                }}
            >
                <Form.Item label="Clave de Elector" name="electorKey">
                    <Input disabled/>
                </Form.Item>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="Nombre *"
                            name="name"
                            rules={isEditMode ? [
                                {required: true, message: "El nombre es obligatorio."},
                                {validator: validateIneMatch}
                            ] : []}
                            dependencies={['electorKey', 'firstLastName', 'secondLastName']}
                        >
                            <Input placeholder="Nombre" disabled={!isEditMode} onInput={(e) => {
                                e.target.value = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '').toUpperCase();
                            }}/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="Apellido Paterno *"
                            name="firstLastName"
                            rules={
                                isEditMode
                                    ? [
                                        {required: true, message: "El apellido paterno es obligatorio."},
                                        {validator: validateIneMatch}
                                    ]
                                    : []
                            }
                            dependencies={['electorKey', 'name', 'secondLastName']}
                        >
                            <Input placeholder="Apellido Paterno" disabled={!isEditMode} onInput={(e) => {
                                e.target.value = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '').toUpperCase();
                            }}/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item 
                            label={
                                <span>
                                    Apellido Materno (o XX){' '}
                                    <Tooltip title="En caso de no contar con segundo apellido debe escribir XX">
                                        <QuestionCircleOutlined style={{color: '#9f1717'}} />
                                    </Tooltip>
                                </span>
                            } 
                            name="secondLastName"
                            rules={isEditMode ? [{validator: validateIneMatch}] : []}
                            dependencies={['electorKey', 'name', 'firstLastName']}
                        >
                            <Input placeholder="Apellido Materno (Opcional)" disabled={!isEditMode} onInput={(e) => {
                                e.target.value = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '').toUpperCase();
                            }}/>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    label="Teléfono (10 dígitos con lada) *"
                    name="phone"
                    rules={
                        isEditMode
                            ? [
                                {required: true, message: 'El teléfono es obligatorio.'},
                                {
                                    validator: async (_, value) => {
                                        if (!value) return Promise.resolve();
                                        const cleanedValue = value.replace(/\D/g, '');
                                        if (cleanedValue.length !== 10) {
                                            return Promise.reject(new Error('Debe contener exactamente 10 dígitos.'));
                                        }
                                        const lada3 = cleanedValue.substring(0, 3);
                                        const lada2 = cleanedValue.substring(0, 2);
                                        const validLada3 = areaCodes.some(ac => ac.areaCode.toString() === lada3);
                                        const validLada2 = ['33', '55', '56', '81'].includes(lada2) && areaCodes.some(ac => ac.areaCode.toString() === lada2);
                                        
                                        if (!validLada3 && !validLada2) {
                                            return Promise.reject(new Error(`La lada no es válida.`));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]
                            : []
                    }
                >
                    <Input placeholder="5551234567" maxLength={10} onInput={(e) => {
                        e.target.value = e.target.value.replace(/\D/g, '');
                    }} disabled={!isEditMode}/>
                </Form.Item>

                <Form.Item
                    label="Calle *"
                    name="street"
                    rules={isEditMode ? [{required: true, message: "La calle es obligatoria."}] : []}
                >
                    <Input placeholder="Ingrese la calle" disabled={!isEditMode} onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                </Form.Item>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Número Exterior *"
                            name="exteriorNumber"
                            rules={
                                isEditMode
                                    ? [{required: true, message: "El número exterior es obligatorio."}]
                                    : []
                            }
                        >
                            <Input placeholder="Número Exterior" disabled={!isEditMode} onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label="Número Interior" name="interiorNumber">
                            <Input placeholder="Número Interior (Opcional)" disabled={!isEditMode} onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            label={
                                <span>
                                    Referencia{' '}
                                    <Tooltip title="Ejemplos: 'Casa blanca con portón negro', 'Frente a la tienda Oxxo', 'Entre calle 5 y 7'">
                                        <QuestionCircleOutlined style={{color: '#9f1717'}} />
                                    </Tooltip>
                                </span>
                            }
                            name="reference"
                            rules={[
                                {max: 255, message: "La referencia no puede tener más de 255 caracteres"},
                                {required: false}
                            ]}
                        >
                            <Input placeholder="Referencia" disabled={!isEditMode} onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                        {/* TEMPORAL: Validación de rango de código postal deshabilitada temporalmente */}
                        <Form.Item
                            label="Código Postal *"
                            name="zipCode"
                            rules={[
                                {required: true, message: 'El código postal es obligatorio.'},
                                {
                                    pattern: /^\d{5}$/,
                                    message: 'Debe contener exactamente 5 dígitos numéricos.'
                                },
                            ]}
                        >
                            <Input
                                placeholder="Código Postal"
                                maxLength={5}
                                disabled={!isEditMode}
                                onInput={(e) => {
                                    e.target.value = e.target.value.replace(/\D/g, '');
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="Colonia *"
                            name="neighborhood"
                            rules={
                                isEditMode ? [{required: true, message: "Este campo es obligatorio."}] : []
                            }
                        >
                            <Input placeholder="Colonia" disabled={!isEditMode} onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item label="Municipio/Delegación" name="municipalityName">
                            <Input disabled/>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item label="Estado" name="entityName">
                    <Input disabled/>
                </Form.Item>

                <Form.Item label="Sección" name="section">
                    <Input disabled value={`Sección ${form.getFieldValue("section")}`}/>
                </Form.Item>

                {(mode === "view" && "attendanceAt" in person) &&
                    <Form.Item label="Fecha de asistencia" name="assistance" initialValue={`${person.attendanceAt ? formatDate(person.attendanceAt) : ''}`}>
                        <Input disabled value={`${person.attendanceAt ? formatDate(person.attendanceAt) : ''}`}/>
                    </Form.Item>
                }

                <Form.Item
                    label="Referenciado por (Clave INE)"
                    name="referredByIneKey"
                    rules={isEditMode ? [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                return electorKeyValidator.validate(value) ?
                                    Promise.resolve() :
                                    Promise.reject(new Error('Clave de elector inválida.'));
                            },
                        },
                    ] : []}
                >
                    <Input placeholder="Clave INE de quien lo refirió (opcional)" maxLength={18} disabled={!isEditMode} onInput={(e) => e.target.value = e.target.value.toUpperCase()} />
                </Form.Item>

                {isEditMode ? (
                    <Form.Item
                        label={
                            <span>
                                Ubicación del domicilio{' '}
                                <Tooltip title="Mueve el mapa para centrar el marcador en la ubicación exacta del domicilio del convencido">
                                    <QuestionCircleOutlined style={{color: '#9f1717'}} />
                                </Tooltip>
                            </span>
                        }
                        name="latLng"
                    >
                        <div style={{height: '300px', width: '100%', position: 'relative'}}>
                            <MapContainer
                                key={mapKey}
                                center={mapCenter}
                                zoom={16}
                                style={{height: '100%', width: '100%'}}
                                whenReady={(map) => {
                                    const center = map.target.getCenter();
                                    
                                    // Setear latLng inicial
                                    form.setFieldsValue({latLng: `${center.lat},${center.lng}`});
                                    
                                    map.target.on('moveend', () => {
                                        const newCenter = map.target.getCenter();
                                        form.setFieldsValue({latLng: `${newCenter.lat},${newCenter.lng}`});
                                    });
                                    
                                    setTimeout(() => map.target.invalidateSize(), 100);
                                }}
                            >
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                />
                            </MapContainer>
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 1000,
                                pointerEvents: 'none',
                                marginTop: '-20px'
                            }}>
                                <EnvironmentFilled style={{fontSize: '40px', color: '#ff4d4f'}} />
                            </div>
                            <Button
                                onClick={requestLocation}
                                style={{
                                    position: 'absolute',
                                    bottom: '20px',
                                    right: '20px',
                                    zIndex: 1000,
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 0,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                    backgroundColor: '#9f1717',
                                    borderColor: '#9f1717'
                                }}
                            >
                                <AimOutlined style={{fontSize: '20px', color: '#fff'}} />
                            </Button>
                        </div>
                    </Form.Item>
                ) : (
                    person.latLng && (
                        <Form.Item label="Ubicación de registro">
                            <div style={{height: '300px', width: '100%', position: 'relative'}}>
                                <MapContainer
                                    center={person.latLng.split(',').map(Number)}
                                    zoom={16}
                                    style={{height: '100%', width: '100%'}}
                                    dragging={false}
                                    scrollWheelZoom={false}
                                    doubleClickZoom={false}
                                    zoomControl={false}
                                    whenReady={(map) => {
                                        setTimeout(() => map.target.invalidateSize(), 100);
                                    }}
                                >
                                    <TileLayer
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                    />
                                </MapContainer>
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 1000,
                                    pointerEvents: 'none',
                                    marginTop: '-20px'
                                }}>
                                    <EnvironmentFilled style={{fontSize: '40px', color: '#ff4d4f'}} />
                                </div>
                            </div>
                        </Form.Item>
                    )
                )}

                <Form.Item>
                    <Space style={{width: "100%", justifyContent: "flex-end"}}>
                        <Button onClick={onClose} disabled={loading}>
                            Cerrar
                        </Button>
                        {isEditMode && (
                            <Button type="primary" htmlType="submit" disabled={loading}>
                                Guardar Cambios
                            </Button>
                        )}
                    </Space>
                </Form.Item>
            </Form>

            <Modal
                open={modalVisible}
                footer={null}
                closable={false}
                centered
                onCancel={() => {
                    setModalVisible(false)
                    onClose();
                }}
            >
                {modalType === "success" && (
                    <div style={{textAlign: "center"}}>
                        <CheckCircleOutlined style={{color: "#52c41a", fontSize: 48}}/>
                        <Title level={3}>Actualización Exitosa</Title>
                        <Text>{modalMessage}</Text>
                    </div>
                )}
                {modalType === "error" && (
                    <div style={{textAlign: "center"}}>
                        <CloseCircleOutlined style={{color: "#f5222d", fontSize: 48}}/>
                        <Title level={3}>Error</Title>
                        <Text>{modalMessage}</Text>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PersonEditForm;